import { useMemo } from "react";

import { useNavigate } from "@tanstack/react-router";

import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import type { PipelineSearch } from "..";
import { useAccountDetail, useCsmDirectory, usePipeline } from "../api";
import { fmtCur, fmtMRR, monthLabel } from "../format";

import { FirstTermStartEditor } from "./inline/FirstTermStartEditor";
import {
  CsmAssignmentEditor,
  ForecastSelect,
  NotesField,
  RenewalStatusSelect,
  type CsmOption,
} from "./inline/InlineAnnotation";

/**
 * Compact view shown in the quick-mode drawer. Three sections:
 *   1. Header — name, QB-id, status / tier / link badges
 *   2. Renewal snapshot — renewal date + months-out, ARR/MRR with fallback styling, anchor & forecast chips
 *   3. Inline annotation editing — forecast, renewal status, CSM picker, notes
 * A contract roll-up line at the bottom expands the drawer to the full view.
 */
export function AccountQuickInfo({ qbId }: { qbId: number }) {
  const { data, isLoading, error } = useAccountDetail(qbId);
  const pipeline = usePipeline("billing");
  const directory = useCsmDirectory();

  // Assignable CSMs = the HubSpot directory roster (entries with a user_id —
  // i.e. they've signed into cinch_ops and can be hard-assigned), plus anyone
  // already assigned somewhere in the pipeline (covers legacy/ad-hoc CSMs the
  // directory doesn't know), plus this account's HubSpot suggestion.
  const csmOptions = useMemo<CsmOption[]>(() => {
    const map = new Map<string, string>();
    for (const e of directory.data?.entries ?? []) {
      if (e.user_id) map.set(e.user_id, e.display_name);
    }
    for (const r of pipeline.data?.rows ?? []) {
      if (r.annotation.csm_user_id && r.annotation.csm_display_name) {
        map.set(r.annotation.csm_user_id, r.annotation.csm_display_name);
      }
    }
    const sug = data?.pipeline_row?.annotation.csm_suggestion;
    if (sug?.user_id && sug.display_name) map.set(sug.user_id, sug.display_name);
    return Array.from(map, ([user_id, display_name]) => ({ user_id, display_name }));
  }, [directory.data, pipeline.data, data]);

  if (isLoading) {
    return (
      <div className="space-y-3 p-5">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex justify-between gap-3">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-32" />
          </div>
        ))}
      </div>
    );
  }
  if (error || !data?.pipeline_row) {
    return (
      <div className="p-5 text-sm text-muted-foreground">
        Couldn't load account #{qbId}.
      </div>
    );
  }
  const row = data.pipeline_row;
  const merged = row.is_merged;
  // "Anchored" unlocks the session-scoped editors. The anchor is the source
  // of truth; current_session is the normal companion but can lag by one
  // write (the backend resolves it on demand).
  const anchored = !!(row.current_session || row.annotation.first_term_start_date);
  const usingFallback = (row.current_mrr ?? 0) <= 0 && (row.last_billed_mrr ?? 0) > 0;
  const effectiveMrr = usingFallback ? row.last_billed_mrr : row.current_mrr;
  const effectiveArr = usingFallback ? row.last_billed_arr : row.current_arr;

  // Contract roll-up
  const contractCount = data.contracts.length;
  const contractTotalMrr = data.contracts.reduce(
    (s, c) => s + (Number(c.mrr_total) || 0),
    0,
  );

  return (
    <div className="flex flex-col gap-5 p-5">
      <Header row={row} merged={merged} />

      <section className="flex flex-col gap-2">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          Renewal anchor
        </div>
        <FirstTermStartEditor
          qbId={qbId}
          annotation={row.annotation}
          firstBilledMonth={row.first_billed_month}
          contractSignedAt={row.contract_signed_at}
          disabled={merged}
        />
      </section>

      <RenewalSnapshot
        row={row}
        effectiveMrr={effectiveMrr}
        effectiveArr={effectiveArr}
        usingFallback={usingFallback}
      />

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            CSM annotations
          </div>
          {row.current_session ? (
            <span
              className="rounded-full bg-brand-light px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand"
              title="Edits to forecast, renewal status, and notes attach to this cycle."
            >
              Cycle · {monthLabel(row.current_session.renewal_period)}
            </span>
          ) : !merged && !anchored ? (
            <span
              className="rounded-full bg-warning-light px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-warning"
              title="Set a renewal anchor above to unlock forecast / notes editing."
            >
              No active cycle
            </span>
          ) : null}
        </div>
        {!merged && !anchored && (
          <div className="rounded-md border border-warning/30 bg-warning-light/40 p-2 text-xs text-warning">
            Set a renewal date in the Renewal anchor section above to start tracking
            this cycle. Forecast, renewal status, and notes are disabled until then.
          </div>
        )}
        {/* Annotation editors are session-scoped: gate on the anchor, not on
            current_session — once anchored, writes succeed (the backend
            creates the cycle's session on anchor set, and resolves it on
            first write if that ever raced/failed). Gating on the session row
            itself deadlocked: it doesn't exist until something writes to it. */}
        <FieldRow label="Forecast">
          <ForecastSelect
            qbId={qbId}
            value={row.current_session?.forecast ?? row.annotation.forecast}
            disabled={merged || !anchored}
          />
        </FieldRow>
        <FieldRow label="Renewal status">
          <RenewalStatusSelect
            qbId={qbId}
            value={row.current_session?.renewal_status ?? row.annotation.renewal_status}
            disabled={merged || !anchored}
          />
        </FieldRow>
        <FieldRow label="CSM" align="start">
          <CsmAssignmentEditor
            qbId={qbId}
            csmUserId={row.annotation.csm_user_id}
            csmPartnerQbId={row.annotation.csm_partner_qb_id}
            csmPartnerName={row.annotation.csm_partner_name}
            options={csmOptions}
            disabled={merged}
          />
        </FieldRow>
        <FieldRow label="Notes" align="start">
          <NotesField
            qbId={qbId}
            value={row.current_session?.notes ?? row.annotation.notes}
            disabled={merged || !anchored}
          />
        </FieldRow>
      </section>

      <ContractRollup
        qbId={qbId}
        count={contractCount}
        totalMrr={contractTotalMrr}
        nextRenewal={row.active_renewal_month ?? row.renewal_month}
      />
    </div>
  );
}

function Header({
  row,
  merged,
}: {
  row: NonNullable<ReturnType<typeof useAccountDetail>["data"]>["pipeline_row"];
  merged: boolean;
}) {
  if (!row) return null;
  return (
    <header className="flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <h2 className="text-lg font-semibold tracking-tight">{row.account_name}</h2>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>QB-{row.qb_id}</span>
          {row.industry_name && <span>· {row.industry_name}</span>}
          {row.closer_name && <span>· Closer {row.closer_name}</span>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {merged ? (
          <Badge variant="neutral" className="italic">N/A · Merged</Badge>
        ) : (
          <StatusBadge value={row.billing_status} />
        )}
        {row.current_tier != null && (
          <Badge variant="info">T{row.current_tier}</Badge>
        )}
        {row.is_reseller && <Badge variant="info">Reseller</Badge>}
      </div>
    </header>
  );
}

function RenewalSnapshot({
  row,
  effectiveMrr,
  effectiveArr,
  usingFallback,
}: {
  row: NonNullable<ReturnType<typeof useAccountDetail>["data"]>["pipeline_row"];
  effectiveMrr: number;
  effectiveArr: number;
  usingFallback: boolean;
}) {
  if (!row) return null;
  const renewalMonth = row.active_renewal_month ?? row.renewal_month;
  const mtr = row.active_months_to_renewal;
  let mtrLabel: string | null = null;
  if (mtr != null) {
    if (mtr === 0) mtrLabel = "This month";
    else if (mtr > 0) mtrLabel = `${mtr} mo out`;
    else mtrLabel = `${-mtr} mo ago`;
  }
  return (
    <section className="flex flex-col gap-3 rounded-md border border-border bg-muted/30 p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">
        Renewal snapshot
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <div>
          <div className="text-xs text-muted-foreground">Renewal</div>
          <div className="font-medium">{monthLabel(renewalMonth)}</div>
          {mtrLabel && <div className="text-xs text-muted-foreground">{mtrLabel}</div>}
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Anchor</div>
          <Badge variant="neutral" className="font-mono">
            {row.annotation.renewal_anchor || "global"}
          </Badge>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">
            {usingFallback ? "Last billed ARR" : "Current ARR"}
          </div>
          <div className={usingFallback ? "font-medium tabular-nums text-muted-foreground" : "font-medium tabular-nums"}>
            {fmtCur(effectiveArr)}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">
            {usingFallback ? "Last billed MRR" : "Current MRR"}
          </div>
          <div className={usingFallback ? "font-medium tabular-nums text-muted-foreground" : "font-medium tabular-nums"}>
            {fmtMRR(effectiveMrr)}
          </div>
        </div>
      </div>
      {usingFallback && row.last_billed_month && (
        <div className="text-xs italic text-muted-foreground">
          Current MRR is $0 — showing last-billed amount from {row.last_billed_month}.
        </div>
      )}
    </section>
  );
}

function ContractRollup({
  qbId,
  count,
  totalMrr,
  nextRenewal,
}: {
  qbId: number;
  count: number;
  totalMrr: number;
  nextRenewal: string | null;
}) {
  const navigate = useNavigate();
  if (count === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
        No contracts on this account.
      </div>
    );
  }
  return (
    <section className="flex items-center justify-between rounded-md border border-border bg-card p-3 text-sm">
      <div className="flex flex-col gap-0.5">
        <div className="font-medium">
          {count} contract{count !== 1 ? "s" : ""}
        </div>
        <div className="text-xs text-muted-foreground">
          {fmtMRR(totalMrr)} total MRR · next {monthLabel(nextRenewal)}
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() =>
          navigate({
            to: "/renewals/pipeline",
            // Preserve filters + the Setup-tab `setup` flag; only flip the
            // drawer into expanded mode for this account.
            search: (prev: PipelineSearch) => ({
              ...prev,
              account: qbId,
              expanded: true,
            }),
          })
        }
      >
        View {count} contract{count !== 1 ? "s" : ""} →
      </Button>
    </section>
  );
}

function FieldRow({
  label,
  align = "center",
  children,
}: {
  label: string;
  align?: "center" | "start";
  children: React.ReactNode;
}) {
  return (
    <div
      className={`grid grid-cols-[7rem_1fr] gap-3 ${align === "start" ? "items-start" : "items-center"}`}
    >
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div>{children}</div>
    </div>
  );
}
